/**
 * Thin axios client for configuration-hub-api's preview endpoints, used by the
 * admin portal (staff-only) to create preview orgs of any organization.
 *
 * Cross-org access: the admin portal's internal-auth token carries no org, so
 * configuration-hub-api takes the source org from the `x-ivy-org-id` header
 * (src/util/auth.ts resolveAuth). Internal callers also name the person to
 * invite (`requester`), since they are not a user of that org.
 *
 * Types mirror configuration-hub-api openapi.yml (Preview, CreatePreviewRequest).
 */
import axios, { AxiosInstance } from 'axios'

import { config } from '../configs/api.config'

import { configureClient } from './api-utils'

export type PreviewStatus =
  | 'creating_org'
  | 'copying_configuration'
  | 'inviting'
  | 'ready'
  | 'failed'

export interface Preview {
  id: string
  name: string
  status: PreviewStatus
  source_org_id: string
  preview_org_id?: string
  sync_job_id?: string
  sync_status?: 'succeeded' | 'partial'
  requested_by: { user_id?: string; email?: string }
  created_at: string
  updated_at: string
  error?: string
}

export interface CreatePreviewParams {
  sourceOrgId: string
  name?: string
  requester: {
    email: string
    display_name?: string
    language?: 'de' | 'en'
  }
}

let client: AxiosInstance | undefined

const getClient = (): AxiosInstance => {
  if (!client) {
    client = configureClient(axios.create(), {
      baseURL: config.CONFIGURATION_HUB
    })
  }

  return client
}

const orgHeader = (orgId: string) => ({ 'x-ivy-org-id': orgId })

export const createPreview = async ({
  sourceOrgId,
  name,
  requester
}: CreatePreviewParams): Promise<Preview> => {
  const { data } = await getClient().post<Preview>(
    '/v1/previews',
    { name: name || undefined, requester },
    { headers: orgHeader(sourceOrgId) }
  )

  return data
}

export const getPreview = async ({
  sourceOrgId,
  previewId
}: {
  sourceOrgId: string
  previewId: string
}): Promise<Preview> => {
  const { data } = await getClient().get<Preview>(
    `/v1/previews/${encodeURIComponent(previewId)}`,
    { headers: orgHeader(sourceOrgId) }
  )

  return data
}

export const TERMINAL_PREVIEW_STATUSES: ReadonlySet<PreviewStatus> = new Set([
  'ready',
  'failed'
])
